import AppForms from "@toothfairy/shared-ui/AppForms";
import AppUser from "@toothfairy/shared-ui/AppUser";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import AppToast from "@toothfairy/shared-ui/AppToast";
import QaHiringTemplateView from "./QaHiringTemplateView";
import connectionsHelper from "../../../../../API/Connections/connectionsHelper";
import useWorkspaces from "../../../../../Hooks/useWorkspaces";
import execEnvsHelper from "../../../../../API/ExecutionEnvironments/execEnvsHelper";
import AgentFunctions from "../../../../../API/AgentFunctions";
import functionsHelper from "../../../../../API/Functions/functionsHelper";
import Agents from "../../../../../API/Agents";
import Prompts from "../../../../../API/Prompts";
import ToothFairyLoadingPlaceholder from "../../../../../Components/ToothFairyComponents/ToothFairyLoadingPlaceholder";

const QaHiringTemplate = ({
  item,
  setIsHiringTemplateModalOpen,
  setShowPreselectionModal,
  onAgentCreated,
}) => {
  const { t } = useTranslation();
  const [validationSchema, setValidationSchema] = useState(null);
  const [initialFormValues, setInitialFormValues] = useState({});
  const [excludedResources, setExcludedResources] = useState([]);
  const [excludedSubAgents, setExcludedSubAgents] = useState([]);
  const [formKey, setFormKey] = useState(Date.now());

  const AppToasty = AppToast.useToast();
  const [isLoading, setIsLoading] = useState(false);
  const { getActiveWorkspace, loadAgents } = useWorkspaces();
  const { state, dispatch } = AppStateManager.useAppStateManager();
  const { user } = AppUser.useUser();
  const ref = useRef();
  const fields = item?.agentHiringTemplate?.fields;
  const resources = item?.agentHiringTemplate?.resources;
  const handleTick = (u, _v) => {
    if (_v == false) {
      setExcludedResources([...excludedResources, u?.id]);
    } else {
      setExcludedResources(excludedResources.filter((i) => i !== u?.id));
    }
  };

  const handleSubAgentTick = (subAgent, isSelected) => {
    if (isSelected == false) {
      setExcludedSubAgents([...excludedSubAgents, subAgent?.id]);
    } else {
      setExcludedSubAgents(excludedSubAgents.filter((i) => i !== subAgent?.id));
    }
  };

  function generateYupSchema(fields) {
    let initialValues = {};

    // Handle case where fields is undefined or empty - don't set validationSchema at all
    if (!fields || fields.length === 0) {
      setInitialFormValues({});
      // Don't set a validation schema - Formik will be valid by default
      setValidationSchema(null);
      return null;
    }

    let schema = {};
    fields.forEach((field) => {
      schema[field.name] = AppForms.yup
        .string()
        .nullable()
        .required("Selection required");
      initialValues[field.name] = null;
    });
    const fullSchema = AppForms.yup.object().shape(schema);
    setValidationSchema(fullSchema);
    setInitialFormValues(initialValues);
    return fullSchema;
  }

  const convertTemplateToSchema = (fields) => {
    return generateYupSchema(fields);
  };
  const entityCleanup = (e) => {
    delete e?._lastChangedAt;
    delete e?.createdAt;
    delete e?.updatedAt;
    delete e?._deleted;
    delete e?.__typename;
    delete e?.id;
    delete e?._version;
    delete e?.parentToolID;
    return e;
  };

  // Helper function to hire a sub-agent using its embedded hiring template
  const hireSubAgent = async (subAgent, parentValues, userId, workspaceId) => {
    const subResources = subAgent.hiringTemplate?.resources || {};

    const subFunctions = [];
    const subPrompts = [];
    const newExecEnvs = [];
    const newDataSources = [];

    try {
      // Fetch the full agent data from the hireable workspace
      const agentResult = await Agents.getAgent(subAgent.id);
      const fullAgentData = agentResult?.data?.getAgent;

      if (!fullAgentData) {
        throw new Error(`Sub-agent ${subAgent.id} not found`);
      }

      // Clean the agent data (remove internal fields)
      const cleanedAgentData = entityCleanup({ ...fullAgentData });

      // Process sub-agent functions
      for (const func of subResources?.functions || []) {
        const funcData = await functionsHelper.getAgentFunction(func.id);
        let cleanedFunc = entityCleanup(funcData?.data?.getAgentFunction);

        if (func?.requiredSecrets?.length > 0) {
          cleanedFunc["authorisationID"] =
            parentValues[func?.requiredSecrets[0]];
        }
        cleanedFunc["workspaceID"] = workspaceId;

        const newFunc = await functionsHelper.createAgentFunction(
          cleanedFunc,
          userId,
        );
        subFunctions.push(newFunc?.data?.createAgentFunction?.id);
      }

      // Process sub-agent execution environments
      for (const execEnv of subResources?.executionEnvironments || []) {
        const execEnvData = await execEnvsHelper.getCustomCodeExecutionEnvironment(
          execEnv.id,
        );
        let cleanedExecEnv = entityCleanup(
          execEnvData?.data?.getCustomCodeExecutionEnvironment,
        );

        if (execEnv?.requiredSecrets?.length > 0) {
          cleanedExecEnv["customExecutionSecrets"] = JSON.stringify(
            execEnv?.requiredSecrets.map((secret) => {
              return `${workspaceId}/${parentValues[secret]}`;
            }),
          );
        }
        cleanedExecEnv["workspaceID"] = workspaceId;

        const newExecEnv = await execEnvsHelper.createCustomCodeExecutionEnvironment(
          cleanedExecEnv,
          userId,
        );
        newExecEnvs.push(
          newExecEnv?.data?.createCustomCodeExecutionEnvironment?.id,
        );
      }

      // Process sub-agent data sources
      for (const dataSource of subResources?.dataSources || []) {
        let dbConnection = await connectionsHelper.getDatabaseConnection(
          dataSource.id,
        );
        dbConnection = entityCleanup(dbConnection?.data?.getDatabaseConnection);

        if (dataSource?.requiredSecrets?.length > 0) {
          dbConnection["passwordSecret"] = `${workspaceId}/${
            parentValues[dataSource?.requiredSecrets[0]]
          }`;
        }
        dbConnection["workspaceID"] = workspaceId;

        const newDbConnection = await connectionsHelper.createDatabaseConnection(
          dbConnection,
        );
        newDataSources.push(
          newDbConnection?.data?.createDatabaseConnection?.id,
        );
      }

      // Process sub-agent prompts
      for (const prompt of subResources?.prompts || []) {
        const promptData = await Prompts.getCustomPrompt(prompt.id);
        let cleanedPrompt = entityCleanup(promptData?.data?.getCustomPrompt);
        cleanedPrompt["workspaceID"] = workspaceId;
        cleanedPrompt["availableToAgents"] = [];

        const newPrompt = await Prompts.createPrompt(cleanedPrompt);
        subPrompts.push({
          id: newPrompt?.id,
          originalId: prompt.id,
        });
      }

      // Create the sub-agent with ALL fields from the original agent
      const subAgentData = {
        ...cleanedAgentData,
        workspaceID: workspaceId,
        parentAgentID: item?.id,
        agentFunctions:
          subFunctions.length > 0
            ? subFunctions
            : cleanedAgentData.agentFunctions || [],
        customCodeExecutionEnvironments:
          newExecEnvs.length > 0
            ? newExecEnvs
            : cleanedAgentData.customCodeExecutionEnvironments || [],
        agentHiringTemplate: null,
        parentChatID: null,
      };

      // Ensure required String fields have valid values
      if (!subAgentData.label)
        subAgentData.label = subAgent.name || "Untitled Agent";
      if (!subAgentData.interpolationString)
        subAgentData.interpolationString = "";
      if (!subAgentData.goals) subAgentData.goals = "";

      // Convert AWSJSON fields back to strings (AppSync parses them into objects on read)
      const awsJsonFields = [
        "allowedTopics",
        "staticDocs",
        "customModerations",
        "functionContext",
        "metadata",
        "agentDesktopCredentials",
        "agentFnParams",
        "jsonOutputStructure",
        "predictedPlanJson",
      ];
      awsJsonFields.forEach((field) => {
        if (subAgentData[field] && typeof subAgentData[field] === "object") {
          subAgentData[field] = JSON.stringify(subAgentData[field]);
        }
      });

      const newSubAgent = await Agents.createAgent(subAgentData, userId);
      const newSubAgentId = newSubAgent?.data?.createAgent?.id;

      // Associate prompts with sub-agent
      for (const promptInfo of subPrompts) {
        await Prompts.updatePrompt(promptInfo.id, [
          { property: "availableToAgents", value: [newSubAgentId] },
        ]);
      }

      return { id: newSubAgentId, name: subAgent.name };
    } catch (error) {
      console.error(`Error hiring sub-agent ${subAgent.name}:`, error);
      // Rollback created resources
      for (const funcId of subFunctions) {
        await functionsHelper.deleteAgentFunction(funcId);
      }
      for (const envId of newExecEnvs) {
        await execEnvsHelper.deleteCustomCodeExecutionEnvironment(envId);
      }
      for (const dsId of newDataSources) {
        await connectionsHelper.deleteDatabaseConnection(dsId);
      }
      for (const promptInfo of subPrompts) {
        await Prompts.deletePrompt(promptInfo.id);
      }
      throw error;
    }
  };

  const handleFormSubmission = async (values) => {
    const new_functions = [];
    const new_code_exec_envs = [];
    const new_datasources = [];
    const new_prompts = [];
    const new_sub_agents = [];
    console.log("workspaceID", getActiveWorkspace()?.id);
    try {
      setIsLoading(true);

      // First, hire any selected sub-agents (for orchestrators)
      const subAgents = resources?.agents || [];
      for (const subAgent of subAgents) {
        if (
          !excludedSubAgents?.includes(subAgent.id) &&
          subAgent.hiringTemplate
        ) {
          console.log(`Hiring sub-agent: ${subAgent.name} (${subAgent.id})`);

          // Create the sub-agent using its embedded hiring template
          const subAgentData = await hireSubAgent(
            subAgent,
            values,
            user?.id,
            getActiveWorkspace()?.id,
          );
          if (subAgentData) {
            new_sub_agents.push({
              id: subAgentData.id,
              originalId: subAgent.id,
              name: subAgent.name,
            });
          }
        }
      }

      for (i in resources?.dataSources) {
        let dataSource = resources?.dataSources[i];

        if (!excludedResources?.includes(dataSource.id)) {
          db_connection = await connectionsHelper.getDatabaseConnection(
            dataSource.id,
          );
          db_connection = entityCleanup(
            db_connection?.data?.getDatabaseConnection,
          );

          db_connection["passwordSecret"] = `${getActiveWorkspace()?.id}/${
            values[dataSource?.requiredSecrets[0]]
          }`;
          db_connection["workspaceID"] = getActiveWorkspace()?.id;

          const new_db_connection = await connectionsHelper.createDatabaseConnection(
            db_connection,
          );
          new_datasources.push(
            new_db_connection?.data?.createDatabaseConnection?.id,
          );
        }
      }
      for (i in resources?.executionEnvironments) {
        let executionEnv = resources?.executionEnvironments[i];

        if (!excludedResources?.includes(executionEnv.id)) {
          execution_env = await execEnvsHelper.getCustomCodeExecutionEnvironment(
            executionEnv.id,
          );
          execution_env = entityCleanup(
            execution_env?.data?.getCustomCodeExecutionEnvironment,
          );
          if (executionEnv?.requiredSecrets?.length > 0) {
            execution_env["customExecutionSecrets"] = JSON.stringify(
              executionEnv?.requiredSecrets.map((secret) => {
                return `${getActiveWorkspace()?.id}/${values[secret]}`;
              }),
            );
          }

          execution_env["workspaceID"] = getActiveWorkspace()?.id;

          const new_execution_env = await execEnvsHelper.createCustomCodeExecutionEnvironment(
            execution_env,
            user?.id,
          );
          new_code_exec_envs.push(
            new_execution_env?.data?.createCustomCodeExecutionEnvironment?.id,
          );
        }
      }
      for (i in resources?.functions) {
        let AIfunction = resources?.functions[i];

        if (!excludedResources?.includes(AIfunction.id)) {
          ai_function = await functionsHelper.getAgentFunction(AIfunction.id);
          ai_function = entityCleanup(ai_function?.data?.getAgentFunction);
          if (AIfunction?.requiredSecrets?.length > 0) {
            ai_function["authorisationID"] =
              values[AIfunction?.requiredSecrets[0]];
          }

          ai_function["workspaceID"] = getActiveWorkspace()?.id;

          const new_ai_function = await functionsHelper.createAgentFunction(
            ai_function,
            user?.id,
          );
          new_functions.push(new_ai_function?.data?.createAgentFunction?.id);
        }
      }
      for (const prompt of resources?.prompts || []) {
        if (!excludedResources?.includes(prompt.id)) {
          const promptData = await Prompts.getCustomPrompt(prompt.id);
          let cleanedPrompt = entityCleanup(promptData?.data?.getCustomPrompt);

          cleanedPrompt["workspaceID"] = getActiveWorkspace()?.id;
          cleanedPrompt["availableToAgents"] = [];

          const newPrompt = await Prompts.createPrompt(cleanedPrompt);
          new_prompts.push({
            id: newPrompt?.id,
            originalId: prompt.id,
          });
        }
      }

      const purifiedItem = entityCleanup(item);

      const newAgent = {
        ...purifiedItem,
        agentType: "Chatbot",
        agentHiringTemplate: null,
        parentAgentID: item?.id,
        parentChatID: null,
        workspaceID: getActiveWorkspace()?.id,
        agentFunctions: new_functions,
        customCodeExecutionEnvironments: new_code_exec_envs,
        agentsPool:
          new_sub_agents.length > 0 ? new_sub_agents.map((sa) => sa.id) : [],
      };

      // Convert AWSJSON fields back to strings (AppSync parses them into objects on read)
      const awsJsonFields = [
        "allowedTopics",
        "staticDocs",
        "customModerations",
        "functionContext",
        "metadata",
        "agentDesktopCredentials",
        "agentFnParams",
        "jsonOutputStructure",
        "predictedPlanJson",
      ];
      awsJsonFields.forEach((field) => {
        if (newAgent[field] && typeof newAgent[field] === "object") {
          newAgent[field] = JSON.stringify(newAgent[field]);
        }
      });

      const new_agent = await Agents.createAgent(newAgent, user?.id);

      for (const promptInfo of new_prompts) {
        await Prompts.updatePrompt(promptInfo.id, [
          {
            property: "availableToAgents",
            value: [new_agent?.data?.createAgent?.id],
          },
        ]);
      }

      const workspaceID = getActiveWorkspace()?.id;

      await loadAgents(workspaceID);

      const promptsResult = await Prompts.getCustomPromptsByWorkspaceID(
        workspaceID,
      );
      const prompts = promptsResult?.data?.customPromptsByWorkspace?.items?.filter(
        (u) => u?._deleted != true,
      );
      dispatch("SET_CUSTOM_PROMPTS", prompts);

      const execEnvsResult = await execEnvsHelper.getCustomCodeExecutionEnvironments(
        workspaceID,
      );
      const execEnvs = execEnvsResult?.data?.customCodeExecutionEnvironmentsByWorkspace?.items?.filter(
        (u) => u?._deleted != true,
      );
      dispatch("SET_EXEC_ENVS", execEnvs);

      const functionsResult = await functionsHelper.getAgentFunctions(
        workspaceID,
      );
      const agentFunctions = functionsResult?.data?.functionsByWorkspace?.items?.filter(
        (u) => u?._deleted != true,
      );
      dispatch("SET_AGENTS_FUNCTIONS", agentFunctions);

      AppToasty.show(t("toasts.agents.saved"), {
        placement: "bottom",
        type: "success",
      });
      const createdAgentId = new_agent?.data?.createAgent?.id;
      if (createdAgentId && onAgentCreated) {
        onAgentCreated(createdAgentId);
      } else {
        setIsHiringTemplateModalOpen(false);
        setShowPreselectionModal(false);
      }
    } catch (error) {
      console.error(error);

      AppToasty.show(t("toasts.agents.creationFailed"), {
        placement: "bottom",
        type: "danger",
      });
      for (const id of new_functions) {
        await functionsHelper.deleteAgentFunction(id);
      }
      for (const id of new_code_exec_envs) {
        await execEnvsHelper.deleteCustomCodeExecutionEnvironment(id);
      }
      for (const id of new_datasources) {
        await connectionsHelper.deleteDatabaseConnection(id);
      }
      for (const promptInfo of new_prompts) {
        await Prompts.deletePrompt(promptInfo.id);
      }
      // Rollback hired sub-agents
      for (const subAgentInfo of new_sub_agents) {
        await Agents.deleteAgent(subAgentInfo.id);
      }
      AppToasty.show(t("toasts.resources.rollbackSuccess"), {
        placement: "bottom",
        type: "warning",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // keep track of all ids and then associate them to the agent when created specifically customCodeExecutionEnvironments and agentFunctions
  // create the agent with these fields populated
  const generateValidationSchema = (fields) => {
    try {
      convertTemplateToSchema(fields);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    generateValidationSchema(fields);
  }, [JSON.stringify(fields)]);

  useEffect(() => {
    if (item?.id) setFormKey(Date.now().toString());
  }, [item?.id]);

  return isLoading ? (
    <ToothFairyLoadingPlaceholder caption="messages.settingAgent" />
  ) : (
    <View>
      <AppForms.AppForm
        initialValues={initialFormValues}
        onSubmit={handleFormSubmission}
        validationSchema={validationSchema}
        enableReinitialize
        ref={ref}
        key={formKey}
      >
        <QaHiringTemplateView
          agentHiringTemplate={item?.agentHiringTemplate}
          handleTick={handleTick}
          excludedResources={excludedResources}
          handleSubAgentTick={handleSubAgentTick}
          excludedSubAgents={excludedSubAgents}
          isPlanner={item?.mode === "planner"}
          hasSubAgents={
            (item?.agentHiringTemplate?.resources?.agents || []).length > 0
          }
        />
      </AppForms.AppForm>
    </View>
  );
};

export default QaHiringTemplate;
